import { gql, useMutation, useLazyQuery } from '@apollo/client';
import { Pda } from './components/Pda';
import { Header } from './components/Header';
import { ArrivalRelatedScanComponent, HandleScanEventResult, ArrivalRelatedScanComponentRef } from './components/ArrivalRelatedScanComponent';
import { OrderDetailCard, OrderDetailCardRef } from './components/OrderDetailCardNew';
import { ReturnToWhsSettings } from './components/ReturnToWhsSettings';
import { isTrackingNumber, showTrackingNumberError } from '../../utils/BarCodeUtil';
import { playScanSound } from '../../utils/scanUtils';
import React, { useState, useRef } from 'react';
import { t } from '../../i18n';
import { SettingOutlined } from '@ant-design/icons';
import { getNavBarContainerStyle } from '../../utils/deviceUtils';
import { useReturnToWhsSettingsStore } from '../../store/returnToWhsSettingsStore';

const HANDLE_RETURN_TO_WHS_SCAN_EVENT = gql`
   mutation HandleReturnToWhsScanEvent($barCode: String!) {
     result: handleReturnToWhsScanEvent(barCode: $barCode) {
       success
       nextAction
       overallStatus
       exceptionReasonCode
       deliveryAttempts
       sortingCode
       isAbnormal
       exceptionImageS3KeysJson
       exceptionImageS3UrlsJson
       podImageS3UrlsJson
       taskDate
       routeName
       stopNumberStr
       driverAccountName
       dapName
       destWarehouseArrivedAt
       isPickupArrivalScan
       isDestWarehouseArrived
     }
   }
`;

const HANDLE_AUTO_RETURN_TO_WHS_SCAN_EVENT = gql`
   mutation HandleAutoReturnToWhsScanEvent($barCode: String!, $autoOperateType: String!, $exceptionReasonCode: String, $exceptionImageS3KeysJson: [String!]) {
     result: handleAutoReturnToWhsScanEvent(barCode: $barCode, autoOperateType: $autoOperateType, exceptionReasonCode: $exceptionReasonCode, exceptionImageS3KeysJson: $exceptionImageS3KeysJson) {
       success
       nextAction
       overallStatus
       exceptionReasonCode
       deliveryAttempts
       sortingCode
       isAbnormal
       exceptionImageS3KeysJson
       exceptionImageS3UrlsJson
       podImageS3UrlsJson
       taskDate
       routeName
       stopNumberStr
       driverAccountName
       dapName
       destWarehouseArrivedAt
       isPickupArrivalScan
       isDestWarehouseArrived
     }
   }
`;

const GET_ORDER_STATUS = gql`
   mutation HandleAutoReturnToWhsScanStatusEvent($barCode: String!) {
     result: handleAutoReturnToWhsScanStatusEvent(barCode: $barCode) {
       success
       overallStatus
       isPickupArrivalScan
     }
   }
`;

// 扩展类型，允许 nextAction 字段
interface ReturnToWhsScanEventResult extends HandleScanEventResult {
    nextAction?: string;
    overallStatus?: string;
    exceptionReasonCode?: string;
    deliveryAttempts?: number;
    sortingCode?: string;
    isAbnormal?: boolean;
    exceptionImageS3KeysJson?: string[];
    exceptionImageS3UrlsJson?: string[];
    taskDate?: string;
    routeName?: string;
    stopNumberStr?: string;
    driverAccountName?: string;
    dapName?: string;
    destWarehouseArrivedAt?: number;
    isPickupArrivalScan?: boolean;
    isDestWarehouseArrived?: boolean;
}

const PageBody = () => {
    const [handleReturnToWhsScanEvent] = useMutation(HANDLE_RETURN_TO_WHS_SCAN_EVENT);
    const [handleAutoReturnToWhsScanEvent] = useMutation(HANDLE_AUTO_RETURN_TO_WHS_SCAN_EVENT);
    const [getOrderStatus] = useMutation(GET_ORDER_STATUS);
    const [overlayVisible, setOverlayVisible] = useState(false);
    
    // 获取设置状态
    const { directAcceptDeliveryPackages, directWarehouseEntry } = useReturnToWhsSettingsStore();
    const [overlayData, setOverlayData] = useState<{
        nextAction: string;
        trackingNumber: string;
        overallStatus: string;
        exceptionReasonCode?: string;
        deliveryAttempts?: number;
        sortingCode?: string;
        isAbnormal?: boolean;
        exceptionImageS3Keys?: string[];
        exceptionImageS3UrlsJson?: string[];
        podImageS3UrlsJson?: string[];
        taskDate?: string;
        routeName?: string;
        stopNumberStr?: string;
        driverAccountName?: string;
        dapName?: string;
        destWarehouseArrivedAt?: number;
        isPickupArrivalScan?: boolean;
        isDestWarehouseArrived?: boolean;
        justReturnedToWhs?: boolean;
    } | null>(null);
    const scanComponentRef = useRef<ArrivalRelatedScanComponentRef>(null);
    const orderDetailCardRef = useRef<OrderDetailCardRef>(null);

    const handleScanEvent = async (barCode: string): Promise<ReturnToWhsScanEventResult> => {
        if (!isTrackingNumber(barCode)) {
            // 扫描请求失败时，确保详情页已关闭
            if (overlayVisible && orderDetailCardRef.current) {
                orderDetailCardRef.current.close();
            }
            showTrackingNumberError(barCode);
            return { success: false };
        }

        try {
            // 1. 首先获取当前订单状态
            const { data: orderData } = await getOrderStatus({
                variables: { barCode: barCode }
            });
            
            const statusResult = orderData?.result;
            const currentOrderStatus = statusResult?.overallStatus;
            const isPickupArrivalScan = statusResult?.isPickupArrivalScan;
            
            // 注意：isPickupArrivalScan 可以用于显示包裹是否已进行过揽收到件扫描
            // 这个信息可以帮助用户了解包裹的处理状态
            
            // 2. 根据订单状态和设置决定使用哪种操作
            let data;
            // 检查是否满足直接接收派送包裹的条件
            // 条件：开启了directAcceptDeliveryPackages设置 且 订单状态为待派送或派送中
            if (directAcceptDeliveryPackages && 
                currentOrderStatus && 
                ['WAITING_FOR_DELIVERY', 'DELIVERY_IN_PROGRESS'].includes(currentOrderStatus)) {
                // 调用自动返仓扫描接口
                const result = await handleAutoReturnToWhsScanEvent({
                    variables: {
                        barCode,
                        autoOperateType: 'DIRECT_ACCEPT_DELIVERY',
                        exceptionReasonCode: 'ERWHS008',
                        exceptionImageS3KeysJson: null // 自动操作不需要图片

                    }
                });
                data = result.data;
            }
            // 检查是否满足直接入库的条件
            // 条件：开启了directWarehouseEntry设置 且 订单状态为未入库派送仓的状态
            else if (directWarehouseEntry && 
                     currentOrderStatus &&
                ['CREATED', 'PICKUP_FAILED', 'ARRIVED_DESTINATION_HUB',
                      'ARRIVED_ORIGIN_HUB', 'LINEHAUL_PENDING_SHIPMENT', 'FEEDER_PENDING_SHIPMENT',
                      'PENDING_SHIPMENT', 'IN_TRANSIT'].includes(currentOrderStatus)){
                const result = await handleAutoReturnToWhsScanEvent({
                    variables: {
                        barCode,
                        autoOperateType: 'DIRECT_WAREHOUSE_ENTRY',
                        exceptionReasonCode: null,
                        exceptionImageS3KeysJson: null // 自动操作不需要图片
                    }
                });
                data = result.data;
            } else if (directWarehouseEntry &&
                currentOrderStatus &&  'ARRIVED_WHS' === currentOrderStatus && isPickupArrivalScan === false){
                const result = await handleAutoReturnToWhsScanEvent({
                    variables: {
                        barCode,
                        autoOperateType: 'DIRECT_WAREHOUSE_ENTRY',
                        exceptionReasonCode: null,
                        exceptionImageS3KeysJson: null // 自动操作不需要图片
                    }
                });
                data = result.data;
            } else {
                // 使用普通返仓扫描接口
                const result = await handleReturnToWhsScanEvent({ variables: { barCode } });
                data = result.data;
            }
            const result = data?.result;

            // 如果有结果数据
            if (result) {
                await playScanSound();
                
                // 判断是否是自动返仓操作成功
                const isAutoReturnSuccess = (directAcceptDeliveryPackages && 
                    currentOrderStatus && 
                    ['WAITING_FOR_DELIVERY', 'DELIVERY_IN_PROGRESS'].includes(currentOrderStatus) &&
                    result.overallStatus === 'ARRIVED_WHS');
                
                const newOverlayData = {
                    nextAction: result.nextAction,
                    trackingNumber: barCode,
                    overallStatus: result.overallStatus,
                    exceptionReasonCode: result.exceptionReasonCode,
                    deliveryAttempts: result.deliveryAttempts,
                    sortingCode: result.sortingCode,
                    isAbnormal: result.isAbnormal,
                    exceptionImageS3Keys: result.exceptionImageS3KeysJson,
                    exceptionImageS3UrlsJson: result.exceptionImageS3UrlsJson,
                    podImageS3UrlsJson: result.podImageS3UrlsJson,
                    taskDate: result.taskDate,
                    routeName: result.routeName,
                    stopNumberStr: result.stopNumberStr,
                    driverAccountName: result.driverAccountName,
                    dapName: result.dapName,
                    destWarehouseArrivedAt: result.destWarehouseArrivedAt,
                    isPickupArrivalScan: result.isPickupArrivalScan,
                    isDestWarehouseArrived: result.isDestWarehouseArrived,
                    justReturnedToWhs: isAutoReturnSuccess
                };
                
                // 如果是相同的运单号，先清空数据再设置，强制触发重新渲染
                if (overlayData?.trackingNumber === barCode) {
                    setOverlayData(null);
                    // 使用 setTimeout 确保状态更新完成后再设置新数据
                    setTimeout(() => {
                        setOverlayData(newOverlayData);
                    }, 0);
                } else {
                    setOverlayData(newOverlayData);
                }
                setOverlayVisible(true);
                
                // 如果扫描失败，抛出特殊错误让失败列表记录，但详情页已经弹出
                if (!result.success) {
                    // 获取当前状态的国际化文本
                    const getLocalizedStatus = (status: string) => {
                        return t(`enum.overallStatus.${status}`, status);
                    };
                    
                    // 创建一个特殊的错误对象，直接设置错误信息
                    const baseMessage = t('pda.common.scanFailed');
                    const statusMessage = result.overallStatus 
                        ? `，${t('pda.common.currentStatus')}：${getLocalizedStatus(result.overallStatus)}`
                        : '';
                    const fullMessage = `${baseMessage}${statusMessage}`;
                    
                    const error = new Error(fullMessage);
                    // 添加自定义属性，让错误处理能够直接使用这个消息，并保持详情页打开
                    (error as any).customMessage = fullMessage;
                    (error as any).keepOverlayOpen = true; // 标记保持详情页打开
                    throw error;
                }

                return { success: true, nextAction: result.nextAction };
            }

            // 没有结果数据时，抛出错误让组件记录失败
            throw new Error(t('pda.common.noNextAction'));
        } catch (error) {
            // 检查是否需要关闭详情页
            const shouldKeepOverlayOpen = (error as any)?.keepOverlayOpen;
            
            // 只有在真正的错误情况下才关闭详情页
            // 如果是扫描失败但需要显示详情页的情况，不关闭
            if (!shouldKeepOverlayOpen && overlayVisible && orderDetailCardRef.current) {
                // 检查错误类型，如果是自定义的扫描失败错误且有 customMessage，说明详情页应该保持打开
                const hasCustomMessage = (error as any)?.customMessage;
                if (!hasCustomMessage) {
                    orderDetailCardRef.current.close();
                }
            }
            throw error; // 重新抛出错误，让 ArrivalRelatedScanComponent 处理
        }
    };

    const handleOverlayClose = () => {
        setOverlayVisible(false);
        setOverlayData(null);
    };

    const handleNextActionChanged = (newNextAction: string, shouldClose: boolean = true) => {
        // 当下一步动作改变后，更新列表中的记录
        if (overlayData && scanComponentRef.current) {
            scanComponentRef.current.updateRecord(overlayData.trackingNumber, {
                nextAction: newNextAction
            });
        }
        // 只有在 shouldClose 为 true 时才关闭弹窗
        if (shouldClose) {
            handleOverlayClose();
        }
    };

    const handleNewScanSuccess = (scanData: {
        trackingNumber: string;
        nextAction?: string;
        overallStatus?: string;
        exceptionReasonCode?: string;
        isAbnormal?: boolean;
    }) => {
        // 添加新的成功扫描记录到列表中
        if (scanComponentRef.current) {
            // 创建一个新的成功记录
            const successRecord = {
                value1: scanData.trackingNumber,
                nextAction: scanData.nextAction ? t(`pda.common.exceptionActions.${scanData.nextAction}`, scanData.nextAction) : undefined
            };

            // 调用 ArrivalRelatedScanComponent 的 addSuccessRecord 方法
            scanComponentRef.current.addSuccessRecord(successRecord);
        }
    };

    return (
        <>
            <ArrivalRelatedScanComponent
             ref={scanComponentRef}
             value1Placeholder={t('pda.pages.returnToWhs.trackingNO')}
             value1ColumnTitle={t('pda.pages.returnToWhs.trackingNO')}
             handleScanEvent={handleScanEvent}
             successExtraFields={[{ title: t('pda.common.nextAction'), key: 'nextAction' }]} // 只在需要时传
            />
            {overlayVisible && overlayData && (
                <OrderDetailCard
                    ref={orderDetailCardRef}
                    trackingNumber={overlayData.trackingNumber}
                    overallStatus={overlayData.overallStatus}
                    exceptionReasonCode={overlayData.exceptionReasonCode}
                    deliveryAttempts={overlayData.deliveryAttempts}
                    sortingCode={overlayData.sortingCode}
                    nextAction={overlayData.nextAction}
                    isAbnormal={overlayData.isAbnormal}
                    exceptionImageS3Keys={overlayData.exceptionImageS3Keys}
                    exceptionImageS3UrlsJson={overlayData.exceptionImageS3UrlsJson}
                    podImageS3UrlsJson={overlayData.podImageS3UrlsJson}
                    taskDate={overlayData.taskDate}
                    routeName={overlayData.routeName}
                    stopNumberStr={overlayData.stopNumberStr}
                    driverAccountName={overlayData.driverAccountName}
                    dapName={overlayData.dapName}
                    destWarehouseArrivedAt={overlayData.destWarehouseArrivedAt}
                    isPickupArrivalScan={overlayData.isPickupArrivalScan}
                    isDestWarehouseArrived={overlayData.isDestWarehouseArrived}
                    justReturnedToWhs={overlayData.justReturnedToWhs}
                    onClose={handleOverlayClose}
                    onNextActionChanged={handleNextActionChanged}
                    onNewScanSuccess={handleNewScanSuccess}
                />
            )}
        </>
    );
}

export default function ReturnToWhsNewPage() {
    const [showSettings, setShowSettings] = useState(false);

    // 如果显示设置页面，则渲染设置页面
    if (showSettings) {
        return <ReturnToWhsSettings onBack={() => setShowSettings(false)} />;
    }

    return (
        <div className="w-full h-full flex flex-col gap-2 p-2">
            {/* 顶部导航栏 */}
            <div
                {...getNavBarContainerStyle('relative')}
                style={{ ...(getNavBarContainerStyle().style || {}), paddingTop: 0 }} // ⬅️ 覆盖掉白条
            >
                <Header title={t('pda.pages.returnToWhs.returnToWhsScan')}/>
                <div
                    className="absolute right-2 top-1/2 -translate-y-1/2 w-10 h-10 flex items-center justify-center cursor-pointer"
                    onClick={() => setShowSettings(true)}
                >
                    <SettingOutlined className="text-xl text-gray-600" />
                </div>
            </div>
            <Pda/>
            <PageBody />
        </div>
    );
} 